import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API, formatApiError, tokenStore } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { OrderHistory } from "../../components/OrderHistory";
import { ArrowLeft, CalendarDays, MapPin, Truck, Phone, Mail, Download, Send, CheckCircle2, AlertCircle, FileSignature, Building2, ArrowUpFromLine, Spline, Clock, UserCircle, PackageCheck, PackageX } from "lucide-react";

const fmtPrice = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("fr-FR"); } catch { return "-"; } };

export default function AdminOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);

  // confirm panel
  const [confDate, setConfDate] = useState("");
  const [confNotes, setConfNotes] = useState("");
  const [tolls, setTolls] = useState(false);
  const [tollsAmt, setTollsAmt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [sendingBl, setSendingBl] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/orders/${id}`);
      setOrder(r.data);
      if (r.data.delivery_date_wanted) {
        setConfDate(new Date(r.data.delivery_date_wanted).toISOString().split("T")[0]);
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  const onConfirm = async () => {
    setError(null);
    setMsg(null);
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/orders/${id}/confirm`, {
        delivery_date_confirmed: new Date(confDate).toISOString(),
        admin_notes: confNotes,
        tolls_included: tolls,
        tolls_amount: Number(tollsAmt) || 0,
      });
      setOrder(r.data);
      setMsg("Confirmée. Le client a reçu un email.");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (s) => {
    setError(null);
    try {
      const r = await api.post(`/admin/orders/${id}/status/${s}`);
      setOrder(r.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  const sendBl = async () => {
    setSendingBl(true);
    setError(null); setMsg(null);
    try {
      const r = await api.post(`/admin/orders/${id}/send-bl`);
      setMsg(`BL envoyé à : ${r.data.to.join(", ")}`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSendingBl(false);
    }
  };

  if (loading) return <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>;
  if (!order) return <div className="lc-card p-6">Commande introuvable.</div>;

  const canConfirm = order.status === "NEW";
  const canStartDelivery = ["CONFIRMED", "IN_PROGRESS"].includes(order.status);
  const isDelivered = order.status === "DELIVERED" || order.status === "INVOICED";
  const hasBl = !!order.delivery_proof?.bl_pdf_b64;

  return (
    <div data-testid="admin-order-detail">
      <Link to="/admin/orders" className="text-xs text-[var(--lc-gray)] hover:text-[var(--lc-black)] inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Retour
      </Link>
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="font-mono text-xs text-[var(--lc-gray)]">{order.ref}</div>
          <h1 className="display text-3xl font-extrabold tracking-tight">Commande</h1>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {error && (
        <div data-testid="error-banner" className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}
      {msg && (
        <div data-testid="success-banner" className="mb-4 flex items-start gap-2 bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /><span>{msg}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lc-card p-5 lg:col-span-2 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Info icon={MapPin} label="Adresse" value={order.arrival_address} />
            <Info icon={Truck} label="Distance" value={`${order.distance_km} km`} />
            {order.vendor_name && <Info icon={UserCircle} label="Vendeur" value={order.vendor_name} />}
            <Info icon={CalendarDays} label="Date souhaitée" value={fmtDate(order.delivery_date_wanted)} />
            {order.delivery_date_confirmed && (
              <Info icon={CalendarDays} label="Date confirmée" value={fmtDate(order.delivery_date_confirmed)} highlight />
            )}
          </div>

          {order.recipient && (
            <div>
              <div className="lc-small-label mb-2">Destinataire</div>
              <div className="bg-[var(--lc-surface)] rounded-md p-3 text-sm space-y-1">
                <div className="font-semibold">{order.recipient.first_name} {order.recipient.last_name}</div>
                <div className="text-[var(--lc-gray)]">{order.recipient.address}</div>
                <div className="flex flex-wrap gap-3 text-[var(--lc-gray)]">
                  {order.recipient.phone && <a href={`tel:${order.recipient.phone}`} className="inline-flex items-center gap-1 hover:text-[var(--lc-orange)]"><Phone className="w-3 h-3" />{order.recipient.phone}</a>}
                  {order.recipient.email && <a href={`mailto:${order.recipient.email}`} className="inline-flex items-center gap-1 hover:text-[var(--lc-orange)]"><Mail className="w-3 h-3" />{order.recipient.email}</a>}
                </div>
              </div>
            </div>
          )}

          {order.installer_name && (
            <div>
              <div className="lc-small-label mb-2">Poseur / monteur</div>
              <div className="bg-[var(--lc-surface)] rounded-md p-3 text-sm" data-testid="admin-order-installer">
                <span className="font-semibold">{order.installer_name}</span>
                {order.installer_company && <span className="text-[var(--lc-orange)] font-semibold"> · {order.installer_company}</span>}
                {order.installer_phone && (
                  <a href={`tel:${order.installer_phone}`} className="text-[var(--lc-gray)] hover:text-[var(--lc-orange)] inline-flex items-center gap-1 ml-2">
                    <Phone className="w-3 h-3" />{order.installer_phone}
                  </a>
                )}
              </div>
            </div>
          )}

          {typeof order.kitchen_premounted === "boolean" && (
            <div>
              <div className="lc-small-label mb-2">Cuisine</div>
              <div className="bg-[var(--lc-surface)] rounded-md p-3 text-sm font-semibold" data-testid="admin-order-kitchen">
                {order.kitchen_premounted ? "Prémontée" : "Non prémontée"}
              </div>
            </div>
          )}

          {typeof order.kitchen_paid_full === "boolean" && (
            <div
              data-testid="admin-order-payment-status"
              className={`rounded-md p-3 text-sm font-semibold border flex items-start gap-2 ${order.kitchen_paid_full ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}
            >
              {order.kitchen_paid_full ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
              <span>
                {order.kitchen_paid_full
                  ? "Cuisine réglée à 100% par le client — déclaration du vendeur"
                  : "Cuisine NON réglée à 100% par le client — déclaration du vendeur (risque d'enlèvement impossible : déplacement facturable)"}
              </span>
            </div>
          )}

          {order.access && (
            <div>
              <div className="lc-small-label mb-3">Accès</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <AccessBadge icon={Building2} label="Étage" value={String(order.access.floor || 0)} />
                <AccessBadge icon={ArrowUpFromLine} label="Ascenseur" value={order.access.elevator ? "Oui" : "Non"} active={order.access.elevator} />
                <AccessBadge icon={Spline} label="Colimaçon" value={order.access.spiral_staircase ? "Oui" : "Non"} active={order.access.spiral_staircase} />
                <AccessBadge icon={Clock} label="Créneau imposé" value={order.access.custom_slot ? "Oui" : "Non"} active={order.access.custom_slot} />
              </div>
              {order.access.notes && (
                <div className="mt-3 bg-[var(--lc-surface)] rounded-lg p-3 text-xs text-[var(--lc-dark-gray)]">
                  <span className="font-semibold text-[var(--lc-gray)] mr-1">Notes :</span>
                  {order.access.notes}
                </div>
              )}
            </div>
          )}
          {order.comment && (
            <div>
              <div className="lc-small-label mb-2">Commentaire</div>
              <p className="text-sm">{order.comment}</p>
            </div>
          )}

          <div>
            <div className="lc-small-label mb-2">Colis</div>
            <div className="text-sm">
              {order.total_packages} colis · {order.total_weight_kg} kg · {order.oversized_count} hors gabarit
            </div>
            {order.purchase_order && (
              <a
                data-testid="admin-po-download"
                href={`${API}/orders/${id}/purchase-order.pdf?token=${encodeURIComponent(tokenStore.get() || "")}`}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--lc-orange)] hover:underline"
              >
                Bon de commande PDF ({(order.purchase_order.size / (1024 * 1024)).toFixed(1)} Mo)
              </a>
            )}
            {order.oversized_details?.length > 0 && (
              <ul className="text-xs text-[var(--lc-gray)] mt-2 list-disc pl-4">
                {order.oversized_details.map((d, i) => <li key={i}>{d.type}{d.description ? ` — ${d.description}` : ""}</li>)}
              </ul>
            )}
          </div>

          {order.pickup && (
            <div
              data-testid="admin-order-pickup-summary"
              className={`rounded-md p-3 text-sm font-semibold border flex items-start justify-between gap-2 flex-wrap ${
                order.pickup.status === "FAILED" ? "bg-red-50 text-red-700 border-red-200"
                : order.pickup.status === "PARTIAL" ? "bg-orange-50 text-orange-700 border-orange-200"
                : "bg-green-50 text-green-700 border-green-200"
              }`}
            >
              <span className="inline-flex items-start gap-2">
                {order.pickup.status === "FAILED" ? <PackageX className="w-4 h-4 mt-0.5 shrink-0" /> : <PackageCheck className="w-4 h-4 mt-0.5 shrink-0" />}
                {order.pickup.status === "FAILED"
                  ? "Récupération : déplacement pour rien (à facturer)"
                  : order.pickup.status === "PARTIAL"
                  ? `Récupération partielle (${order.pickup.items.filter((i) => i.checked).length}/${order.pickup.items.length} articles)`
                  : "Récupération complète"}
              </span>
              <span className="flex items-center gap-3">
                <a
                  data-testid="admin-order-pickup-pdf"
                  href={`${API}/admin/orders/${id}/pickup.pdf?token=${encodeURIComponent(tokenStore.get() || "")}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs underline"
                >
                  <Download className="w-3 h-3" /> PDF
                </a>
                <Link data-testid="admin-order-pickup-open" to={`/admin/orders/${id}/pickup`} className="text-xs underline">Détail</Link>
              </span>
            </div>
          )}

          <div className="border-t border-[var(--lc-border)] pt-5">
            <OrderHistory history={order.history} />
          </div>

          {canConfirm && (
            <div className="border-t border-[var(--lc-border)] pt-5">
              <div className="lc-small-label mb-3">Confirmer cette demande</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="lc-label">Date de livraison</label>
                  <input data-testid="admin-confirm-date" type="date" className="lc-input" value={confDate} onChange={(e) => setConfDate(e.target.value)} />
                </div>
                <div className="flex items-end gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input data-testid="admin-tolls-checkbox" type="checkbox" checked={tolls} onChange={(e) => setTolls(e.target.checked)} />
                    Péages
                  </label>
                  {tolls && (
                    <input data-testid="admin-tolls-amount" type="number" min="0" step="1" className="lc-input flex-1" value={tollsAmt} onChange={(e) => setTollsAmt(e.target.value)} placeholder="€" />
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className="lc-label">Notes admin (interne)</label>
                  <textarea data-testid="admin-confirm-notes" rows="2" className="lc-input" value={confNotes} onChange={(e) => setConfNotes(e.target.value)} />
                </div>
              </div>
              <button data-testid="admin-confirm-button" disabled={!confDate || submitting} onClick={onConfirm} className="lc-btn-primary mt-3">
                {submitting ? "Confirmation…" : "Confirmer et notifier le client"}
              </button>
            </div>
          )}

          {canStartDelivery && (
            <div className="border-t border-[var(--lc-border)] pt-5 flex flex-wrap gap-2">
              {order.status === "CONFIRMED" && (
                <button data-testid="admin-start-delivery" onClick={() => setStatus("IN_PROGRESS")} className="lc-btn-secondary">Démarrer la livraison</button>
              )}
              {(order.purchase_order || order.pickup) && (
                <button
                  data-testid="admin-go-pickup-mode"
                  onClick={() => nav(`/admin/orders/${id}/pickup`)}
                  className="lc-btn-secondary inline-flex items-center gap-2 text-[var(--lc-orange)] border-[var(--lc-orange)]/40"
                >
                  <PackageCheck className="w-4 h-4" /> Récupération marchandise
                </button>
              )}
              <button
                data-testid="admin-go-delivery-mode"
                onClick={() => nav(`/admin/orders/${id}/delivery`)}
                className="lc-btn-primary inline-flex items-center gap-2"
              >
                <FileSignature className="w-4 h-4" /> Mode livraison (signature)
              </button>
              <button data-testid="admin-cancel-order" onClick={() => setStatus("CANCELLED")} className="lc-btn-secondary text-red-600 border-red-300">
                Annuler
              </button>
            </div>
          )}

          {isDelivered && hasBl && (
            <div className="border-t border-[var(--lc-border)] pt-5 space-y-3">
              {order.delivery_proof?.comment && (
                <div>
                  <div className="lc-small-label mb-2">Commentaire de livraison</div>
                  <div className="bg-[var(--lc-orange-light)] border-l-4 border-[var(--lc-orange)] p-3 text-sm">
                    {order.delivery_proof.comment}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <a
                  href={`${API}/admin/orders/${id}/bl.pdf?token=${encodeURIComponent(tokenStore.get() || "")}`}
                  target="_blank" rel="noreferrer"
                  data-testid="admin-download-bl"
                  className="lc-btn-secondary inline-flex items-center gap-2"
                >
                  <Download className="w-4 h-4" /> Télécharger le BL
                </a>
                <button data-testid="admin-send-bl" onClick={sendBl} disabled={sendingBl} className="lc-btn-primary inline-flex items-center gap-2">
                  <Send className="w-4 h-4" /> {sendingBl ? "Envoi…" : "Envoyer le BL par email"}
                </button>
                {order.status === "DELIVERED" && (
                  <button
                    data-testid="admin-mark-invoiced"
                    onClick={() => { if (window.confirm("Marquer cette commande comme facturée ?")) setStatus("INVOICED"); }}
                    className="lc-btn-secondary inline-flex items-center gap-2 text-teal-700 border-teal-300"
                  >
                    Marquer comme facturée
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="lc-card p-5 h-fit">
          <div className="lc-small-label mb-1">Prix</div>
          <div className="text-3xl font-black text-[var(--lc-orange)]">{order.on_quote ? "Sur devis" : fmtPrice(order.total_ttc)}</div>
          {order.accounting_ref && (
            <div className="text-xs text-[var(--lc-gray)] mt-1" data-testid="admin-order-accounting-ref">
              Réf. comptable : <span className="font-mono font-bold text-[var(--lc-black)]">{order.accounting_ref}</span>
            </div>
          )}
          {!order.on_quote && <div className="text-xs text-[var(--lc-gray)] mt-1">{fmtPrice(order.total_ht)} HT · TVA {(order.tva_rate * 100).toFixed(0)}%</div>}
          <div className="text-xs text-[var(--lc-gray)] mt-2">Forfait : <span className="font-semibold text-black">{order.package_name}</span></div>
          {order.applied_options?.length > 0 && (
            <div className="mt-4 space-y-1 text-xs">
              <div className="lc-small-label">Détail</div>
              <div className="flex justify-between"><span>Forfait base</span><span className="font-semibold">{fmtPrice(order.base_price_ht)}</span></div>
              {order.applied_options.map((o, i) => (
                <div key={i} className="flex justify-between text-[var(--lc-gray)]">
                  <span>+ {o.name}{o.quantity > 1 ? ` (×${o.quantity})` : ""}</span>
                  <span>{fmtPrice(o.amount)}</span>
                </div>
              ))}
            </div>
          )}
          {order.tolls_included && (
            <div className="mt-3 text-xs text-[var(--lc-gray)]">Péages : {fmtPrice(order.tolls_amount)} (refacturé hors plateforme)</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value, highlight }) {
  return (
    <div className={highlight ? "bg-[var(--lc-orange-light)] p-3 rounded-md" : ""}>
      <div className="lc-small-label flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3 text-[var(--lc-orange)]" /> {label}
      </div>
      <div className="text-sm font-semibold">{value || "—"}</div>
    </div>
  );
}

function AccessBadge({ icon: Icon, label, value, active }) {
  return (
    <div
      className={`flex items-center gap-2.5 p-2.5 rounded-xl border ${
        active ? "bg-[var(--lc-orange-soft)] border-[var(--lc-orange)]/40" : "bg-white border-[var(--lc-border)]"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
          active ? "bg-[var(--lc-orange)] text-white" : "bg-[var(--lc-surface)] text-[var(--lc-gray)]"
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[var(--lc-gray)] font-semibold">{label}</div>
        <div className="text-sm font-bold leading-tight">{value}</div>
      </div>
    </div>
  );
}
